<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ isEditMode ? "Edit Delivery Partner" : "View Delivery Partner" }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isEditMode ? "Update delivery partner details and settings" : "View delivery partner information" }}
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-3 edit-toggle-button"
                      :disabled="isLoading"
                      small
                    >
                      <v-icon left>{{ isEditMode ? "mdi-eye" : "mdi-pencil" }}</v-icon>
                      {{ isEditMode ? "Read Only" : "Edit Mode" }}
                    </v-btn>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Partners
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <v-card-text class="py-12 text-center">
              <health-loader message="Loading delivery partner..." />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error State -->
      <v-row v-else-if="!partner && !isLoading">
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <v-card-text class="py-12 text-center">
              <v-icon size="80" color="error">mdi-alert-circle</v-icon>
              <h3 class="text-h6 grey--text mt-4 mb-2">Delivery Partner Not Found</h3>
              <p class="grey--text mb-4">The requested delivery partner could not be found.</p>
              <v-btn color="primary" @click="$router.back()">
                <v-icon left>mdi-arrow-left</v-icon>
                Go Back
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Form Card -->
      <div v-else>
        <v-row>
          <v-col cols="12">
            <v-card elevation="3" class="form-card">
              <v-card-title class="form-header py-1 px-2">
                <h2 class="form-title">Partner Information</h2>
                <v-spacer></v-spacer>
                <v-chip :color="isEditMode ? 'success' : 'warning'" :outlined="!isEditMode" x-small>
                  <v-icon left x-small>{{ isEditMode ? "mdi-pencil" : "mdi-eye" }}</v-icon>
                  {{ isEditMode ? "Edit Mode" : "Read Only Mode" }}
                </v-chip>
              </v-card-title>

              <v-divider></v-divider>

              <v-card-text class="form-content px-2 py-2">
                <v-form ref="form" v-model="valid" lazy-validation>
                  <!-- Basic Information Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-truck-delivery</v-icon>
                      Basic Information
                    </h3>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.name"
                          label="Partner Name"
                          placeholder="Enter delivery partner name"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :rules="[(v) => !!v || 'Partner name is required']"
                          :error-messages="errors.name"
                          class="professional-field"
                          hide-details="auto"
                          required
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-truck-delivery</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.phone"
                          label="Contact Number"
                          placeholder="Enter contact number"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="errors.phone"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-phone</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="form.url"
                          label="Website URL (Optional)"
                          placeholder="Enter partner website URL"
                          outlined
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="errors.url"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-web</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Additional Information Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                      Additional Information
                    </h3>

                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="form.note"
                          label="Notes (Optional)"
                          placeholder="Enter any additional notes about this delivery partner"
                          outlined
                          rows="3"
                          :disabled="!isEditMode"
                          :readonly="!isEditMode"
                          :error-messages="errors.note"
                          class="professional-field"
                          hide-details="auto"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="grey">mdi-note-text</v-icon>
                          </template>
                        </v-textarea>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Partner Settings Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                      Partner Settings
                    </h3>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-card outlined class="status-card">
                          <v-card-text class="pa-2">
                            <div class="d-flex align-center">
                              <v-icon color="grey" class="mr-3">mdi-check-circle-outline</v-icon>
                              <div class="flex-grow-1">
                                <div class="font-weight-medium mb-1">Partner Status</div>
                                <div class="text-caption grey--text">Set the status for this delivery partner</div>
                              </div>
                              <v-switch
                                v-model="form.is_active"
                                color="success"
                                inset
                                hide-details
                                class="ml-3"
                                :disabled="!isEditMode"
                              ></v-switch>
                            </div>
                            <div class="mt-2">
                              <v-chip small :color="form.is_active ? 'success' : 'warning'" :text-color="form.is_active ? 'white' : 'black'">
                                {{ form.is_active ? "Active" : "Inactive" }}
                              </v-chip>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Partner Analytics Section -->
                  <div class="form-section mb-2">
                    <h3 class="section-title mb-1">
                      <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                      Partner Analytics
                    </h3>

                    <v-row>
                      <v-col cols="12" md="3">
                        <v-card outlined class="analytics-card">
                          <v-card-text class="pa-2 text-center">
                            <v-icon color="info" class="mb-1">mdi-calendar-plus</v-icon>
                            <div class="text-caption font-weight-medium mb-1">Created</div>
                            <div class="text-body-2">{{ formatDate(partner.created_at) }}</div>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <v-col cols="12" md="3">
                        <v-card outlined class="analytics-card">
                          <v-card-text class="pa-2 text-center">
                            <v-icon color="warning" class="mb-1">mdi-calendar-edit</v-icon>
                            <div class="text-caption font-weight-medium mb-1">Last Updated</div>
                            <div class="text-body-2">{{ formatDate(partner.updated_at) }}</div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-form>
              </v-card-text>

              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-if="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <v-chip small :color="hasValidationErrors ? 'error' : 'success'" text-color="white" class="mr-3">
                        <v-icon left x-small>{{ hasValidationErrors ? "mdi-alert-circle" : "mdi-check-circle" }}</v-icon>
                        {{ hasValidationErrors ? "Please fix validation errors" : "Form validation passed" }}
                      </v-chip>
                      <span class="text-caption grey--text">All required fields must be completed</span>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn color="error" outlined class="mr-3 action-button" @click="confirmDelete" :disabled="isSaving || isDeleting">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                      <v-btn color="grey" outlined class="mr-3 action-button" @click="$router.back()" :disabled="isSaving || isDeleting">
                        Cancel
                      </v-btn>
                      <v-btn color="primary" class="action-button" @click="updatePartner" :loading="isSaving" :disabled="isSaving || isDeleting || !valid">
                        <v-icon left>mdi-content-save</v-icon>
                        Update Partner
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: green; font-size: 90px; width: 200px !important">mdi-check-circle</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Delivery partner updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">OK</v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3">mdi-delete-alert</v-icon>
              <h3 class="mb-3">Are you sure you want to delete this delivery partner?</h3>
              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will delete the delivery partner.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="showDeleteDialog = false" :disabled="isDeleting">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn color="error" @click="deletePartner" :loading="isDeleting" :disabled="isDeleting">
              <v-icon left>mdi-delete</v-icon>
              Delete Partner
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "DeliveryPartnerIdPage",

  data: function () {
    return {
      valid: false,
      partner: null,
      form: {
        name: "",
        phone: "",
        url: "",
        note: "",
        is_active: true
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
      isEditMode: false
    };
  },

  computed: {
    hasValidationErrors: function () {
      return Object.keys(this.errors).length > 0 || !this.valid;
    }
  },

  mounted: function () {
    this.fetchPartner();
    if (this.$route.query.edit === "true") {
      this.isEditMode = true;
    }
  },

  methods: {
    toggleEditMode: function () {
      this.isEditMode = !this.isEditMode;
    },

    fetchPartner: function () {
      var self = this;
      self.isLoading = true;
      var partnerId = self.$route.params.id;

      axios.get("/delivery_partners/" + partnerId)
        .then(function (response) {
          if (response.data.success) {
            self.partner = response.data.data;
            self.form = {
              name: self.partner.name || "",
              phone: self.partner.phone || "",
              url: self.partner.url || "",
              note: self.partner.note || "",
              is_active: self.partner.is_active
            };
          } else {
            self.$store.dispatch("errors/setError", {
              message: "Delivery partner not found"
            });
          }
        })
        .catch(function (error) {
          console.error(error);
          if (error.response && error.response.status === 404) {
            self.$store.dispatch("errors/setError", {
              message: "Delivery partner not found"
            });
          } else if (error.response && error.response.status === 401) {
            self.$router.push("/login");
          } else {
            self.$store.dispatch("errors/setError", {
              message: "Error loading delivery partner data"
            });
          }
        })
        .finally(function () {
          self.isLoading = false;
        });
    },

    updatePartner: function () {
      var self = this;

      if (!self.$refs.form.validate()) {
        return;
      }

      self.isSaving = true;
      self.errors = {};

      var partnerData = {
        name: self.form.name,
        phone: self.form.phone || null,
        url: self.form.url || null,
        note: self.form.note || null,
        is_active: self.form.is_active
      };

      var partnerId = self.$route.params.id;

      axios.patch("/delivery_partners/" + partnerId, partnerData)
        .then(function (response) {
          if (response.data.success) {
            self.showSuccessDialog = true;
            self.partner = response.data.data;
          } else {
            self.$store.dispatch("errors/setError", {
              message: response.data.message || "An error occurred while updating the delivery partner."
            });
          }
        })
        .catch(function (error) {
          console.error(error);
          if (error.response) {
            if (error.response.status === 422) {
              self.errors = error.response.data.errors || {};
            } else if (error.response.status === 401) {
              self.$router.push("/login");
            } else {
              self.$store.dispatch("errors/setError", {
                message: error.response.data.message || "An error occurred while updating the delivery partner."
              });
            }
          } else {
            self.$store.dispatch("errors/setError", {
              message: "Network error. Please check your connection and try again."
            });
          }
        })
        .finally(function () {
          self.isSaving = false;
        });
    },

    confirmDelete: function () {
      this.showDeleteDialog = true;
    },

    deletePartner: function () {
      var self = this;
      self.isDeleting = true;

      var partnerId = self.$route.params.id;

      axios.post("/delivery_partners/delete/" + partnerId)
        .then(function (response) {
          if (response.data.success) {
            self.showDeleteDialog = false;
            self.$router.push("/delivery-partners");
          } else {
            self.$store.dispatch("errors/setError", {
              message: response.data.message || "An error occurred while deleting the delivery partner."
            });
          }
        })
        .catch(function (error) {
          console.error(error);
          if (error.response && error.response.status === 401) {
            self.$router.push("/login");
          } else {
            self.$store.dispatch("errors/setError", {
              message: "An error occurred while deleting the delivery partner."
            });
          }
        })
        .finally(function () {
          self.isDeleting = false;
        });
    },

    formatDate: function (dateString) {
      if (!dateString) return "N/A";
      var date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }
  },

  watch: {
    "form.name": function () {
      if (this.errors.name) {
        this.$delete(this.errors, "name");
      }
    },
    "form.phone": function () {
      if (this.errors.phone) {
        this.$delete(this.errors, "phone");
      }
    },
    "form.url": function () {
      if (this.errors.url) {
        this.$delete(this.errors, "url");
      }
    }
  }
};
</script>

<style scoped>
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.edit-toggle-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

.analytics-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: 100px;
}

.analytics-card:hover {
  border-color: var(--v-info-base);
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-title {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

.v-card,
.v-btn,
.professional-field,
.status-card,
.analytics-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
